/* eslint-disable prettier/prettier */
import { useMemo } from 'react';
import classnames from 'classnames';
import { IconButton, Badge } from '@mui/material';

import { MessageCircleIcon, MessageCircleDisabledIcon, CrossCircleIcon } from 'assets';
import { Chat } from 'components';
import { useChat } from 'hooks';
import { ApiWaitingRoomLinkProfile } from 'types';

import styles from './ChatButton.module.scss';

type ChatButtonProps = {
  provider: ApiWaitingRoomLinkProfile;
  className?: string;
  hideToggleButton?: boolean;
};

export function ChatButton({ provider, className, hideToggleButton = false }: ChatButtonProps) {
  const { identity, isChatOpen, conversationsUnreadMessagesData, openChat, closeChat } = useChat();
  const forceLightTheme = localStorage.getItem('app-theme') === null;

  const unreadMessagesCount: number = useMemo(() => {
    return conversationsUnreadMessagesData.get(`$chat-${provider.providerId}-${identity}`)?.unreadMessagesCount ?? 0;
  }, [conversationsUnreadMessagesData, identity, provider]);

  const handleClick = (): void => {
    if (isChatOpen) {
      closeChat();
    } else if (!provider.unavailable && provider.isProviderInQueue) {
      openChat(provider.providerId!, identity);
    }
  };

  return (
    <div className={classnames(styles.chatButton, className)}>
      {hideToggleButton && isChatOpen && <div className={styles.mobileBackdrop} onClick={closeChat} />}
      <Chat className={styles.chat} forceLightTheme={forceLightTheme} />

      {!hideToggleButton && (
        <IconButton disabled={provider.unavailable || !provider.isProviderInQueue} onClick={handleClick}>
          {provider.unavailable || !provider.isProviderInQueue ? (
            <MessageCircleDisabledIcon sx={{ color: 'var(--app-text-muted)' }} />
          ) : (
            <Badge
              badgeContent={unreadMessagesCount}
              className={styles.chatIconBadge}
              color='primary'
              overlap='circular'
            >
              {isChatOpen ? <CrossCircleIcon color='primary' /> : <MessageCircleIcon color='primary' />}
            </Badge>
          )}
        </IconButton>
      )}
    </div>
  );
}
